import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useContent } from "@/context/ContentContext";
import { resolveMedia } from "@/lib/api";

/**
 * Trumpa projektų apžvalga pagrindiniame puslapyje (be modalų).
 */
export default function HomeProjectsTeaser() {
  const { content } = useContent();
  const projects = content.projects || [];
  const teaser = content.homeProjectsTeaser || {};
  const headerRef = useReveal();
  return (
    <section
      data-testid="home-projects-teaser"
      className="bg-[#efedea] py-24 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <div
          className="reveal grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
          ref={headerRef}
        >
          <div className="md:col-span-7">
            <span className="overline">— Projektai</span>
            <h2 className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1a1a1a]">
              {teaser.headlineTop || "Skirtingos pirtys."}
              <br />
              <span className="italic">{teaser.headlineBottom || "Viena filosofija."}</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-md md:ml-auto">
              {teaser.intro || "Skirtingi mastai. Tas pats požiūris – proporcija, šviesa ir tylus santūrumas."}
            </p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {projects.map((p, idx) => (
            <TeaserCard key={p.id} project={p} index={idx} />
          ))}
        </div>

        <div className="mt-20 flex justify-center">
          <Link
            to="/projektai"
            data-testid="home-projects-cta"
            className="btn-primary"
          >
            Visi projektai <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function TeaserCard({ project, index }) {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`home-project-${project.id}`}
      className="reveal group"
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <Link to={`/projektai#${project.slug}`} className="block">
        <div className="img-zoom aspect-[4/5] bg-[#1a1a1a]">
          <img
            src={resolveMedia(project.cover)}
            alt={project.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="mt-6 flex items-start justify-between gap-4">
          <div>
            <span className="overline">{project.label}</span>
            <h3 className="mt-3 font-serif-display text-2xl md:text-3xl leading-tight text-[#1a1a1a]">
              {project.name}
            </h3>
            <p className="mt-2 text-sm text-[#5c5c5c] font-light">
              {project.tagline}
            </p>
          </div>
          <span className="overline whitespace-nowrap mt-2">
            {project.area}
          </span>
        </div>

        <div className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#1a1a1a] border-b border-[#1a1a1a] pb-1 group-hover:text-[#a66e4b] group-hover:border-[#a66e4b] transition-colors">
          Daugiau apie projektą <ArrowUpRight size={14} strokeWidth={1.5} />
        </div>
      </Link>
    </article>
  );
}
